import { useMutation, useQueryClient } from "@tanstack/react-query";

import { apiClient } from "../../api/apiClient";
import {
  getAssessmentByCaseNumberApiV1AssessmentCaseCaseNumberGet,
  saveAssessmentApiV1AssessmentPost,
  updateAssessmentApiV1AssessmentAssessmentIdPatch,
  updateCaseEndpointApiV1CasesCaseIdPatch,
  updateFieldStatusesApiV1AssessmentAssessmentIdFieldStatusPatch,
} from "../../api/generated/sdk.gen";
import type {
  AssessmentColumnNameEnum,
  AssessmentResultStatusEnum,
  BodySaveAssessmentApiV1AssessmentPost,
  BodyUpdateAssessmentApiV1AssessmentAssessmentIdPatch,
  CasesStatusEnum,
} from "../../api/generated/types.gen";
import { CLIENT_KEYS } from "../../constants/clientKeys";
import { buildApiErrorMessage } from "../../utils/notification";

/**
 * Hook for creating/saving a new assessment
 */
export const useCreateAssessment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: BodySaveAssessmentApiV1AssessmentPost) => {
      const response = await saveAssessmentApiV1AssessmentPost({
        body: data,
        client: apiClient,
      });

      if ("error" in response && response.error) {
        throw new Error(
          buildApiErrorMessage("Failed to create assessment", response.error)
        );
      }

      return response.data;
    },
    onSuccess: (_data, variables) => {
      // Invalidate and refetch assessment queries
      queryClient.invalidateQueries({
        queryKey: [CLIENT_KEYS.ASSESSMENTS],
      });

      // Invalidate the specific case assessment query
      if (variables.case_number) {
        queryClient.invalidateQueries({
          queryKey: [CLIENT_KEYS.ASSESSMENTS, "case", variables.case_number],
        });
      }

      // Also invalidate cases as the case status might change
      queryClient.invalidateQueries({
        queryKey: [CLIENT_KEYS.CASES],
      });
    },
    onError: (error) => {
      console.error("Error creating assessment:", error);
    },
  });
};

/**
 * Hook for updating an existing assessment
 */
export const useUpdateAssessment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: {
      assessmentId: string;
      updateData: BodyUpdateAssessmentApiV1AssessmentAssessmentIdPatch;
      caseNumber?: string; // Optional: helps with cache invalidation
    }) => {
      const { assessmentId, updateData } = data;

      const response = await updateAssessmentApiV1AssessmentAssessmentIdPatch({
        path: { assessment_id: assessmentId },
        body: updateData,
        client: apiClient,
      });

      if ("error" in response && response.error) {
        throw new Error(
          buildApiErrorMessage("Failed to update assessment", response.error)
        );
      }

      return { ...response.data, caseNumber: data.caseNumber };
    },
    onSuccess: (_data, variables) => {
      // Invalidate and refetch assessment queries
      queryClient.invalidateQueries({
        queryKey: [CLIENT_KEYS.ASSESSMENTS],
      });

      // If we have the case number, invalidate specific case assessment query
      if (variables.caseNumber) {
        queryClient.invalidateQueries({
          queryKey: [CLIENT_KEYS.ASSESSMENTS, "case", variables.caseNumber],
        });
      }

      // Also invalidate cases as the case status might change
      queryClient.invalidateQueries({
        queryKey: [CLIENT_KEYS.CASES],
      });
    },
    onError: (error) => {
      console.error("Error updating assessment:", error);
    },
  });
};

/**
 * Hook for updating assessment field with simpler interface
 * This is a convenience hook for updating specific assessment fields
 */
export const useUpdateAssessmentField = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: {
      assessmentId: string;
      caseId: string;
      caseNumber?: string;
      field: keyof Omit<
        BodyUpdateAssessmentApiV1AssessmentAssessmentIdPatch,
        "case_id"
      >;
      value: string;
      status?: AssessmentResultStatusEnum;
    }) => {
      const { assessmentId, caseId, caseNumber, field, value, status } = data;

      const updateData: BodyUpdateAssessmentApiV1AssessmentAssessmentIdPatch = {
        case_id: caseId,
        [field]: value,
      };

      if (status) {
        updateData.status = status;
      }

      const response = await updateAssessmentApiV1AssessmentAssessmentIdPatch({
        path: { assessment_id: assessmentId },
        body: updateData,
        client: apiClient,
      });

      if ("error" in response && response.error) {
        throw new Error(
          buildApiErrorMessage("Failed to update assessment field", response.error)
        );
      }

      return { ...response.data, caseNumber };
    },
    onSuccess: (_data, variables) => {
      // Invalidate and refetch assessment queries
      queryClient.invalidateQueries({
        queryKey: [CLIENT_KEYS.ASSESSMENTS],
      });

      // If we have the case number, invalidate specific case assessment query
      if (variables.caseNumber) {
        queryClient.invalidateQueries({
          queryKey: [CLIENT_KEYS.ASSESSMENTS, "case", variables.caseNumber],
        });
      }

      // Also invalidate cases as the case status might change
      queryClient.invalidateQueries({
        queryKey: [CLIENT_KEYS.CASES],
      });
    },
    onError: (error) => {
      console.error("Error updating assessment field:", error);
    },
  });
};

/**
 * Hook for updating assessment status
 * Convenience hook for status updates
 */
export const useUpdateAssessmentStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: {
      assessmentId: string;
      caseId: string;
      caseNumber?: string;
      status: AssessmentResultStatusEnum;
    }) => {
      const { assessmentId, caseId, status } = data;

      const updateData: BodyUpdateAssessmentApiV1AssessmentAssessmentIdPatch = {
        case_id: caseId,
        status,
      };

      const response = await updateAssessmentApiV1AssessmentAssessmentIdPatch({
        path: { assessment_id: assessmentId },
        body: updateData,
        client: apiClient,
      });

      if ("error" in response && response.error) {
        throw new Error(
          buildApiErrorMessage("Failed to update assessment status", response.error)
        );
      }

      return { ...response.data, caseNumber: data.caseNumber };
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: [CLIENT_KEYS.ASSESSMENTS],
      });

      if (variables.caseNumber) {
        queryClient.invalidateQueries({
          queryKey: [CLIENT_KEYS.ASSESSMENTS, "case", variables.caseNumber],
        });
      }

      queryClient.invalidateQueries({
        queryKey: [CLIENT_KEYS.CASES],
      });
    },
    onError: (error) => {
      console.error("Error updating assessment status:", error);
    },
  });
};

/**
 * Hook for bulk updating multiple assessment fields
 */
export const useBulkUpdateAssessment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: {
      assessmentId: string;
      caseId: string;
      caseNumber?: string;
      updates: Partial<
        Omit<BodyUpdateAssessmentApiV1AssessmentAssessmentIdPatch, "case_id">
      >;
    }) => {
      const { assessmentId, caseId, updates } = data;

      const updateData: BodyUpdateAssessmentApiV1AssessmentAssessmentIdPatch = {
        case_id: caseId,
        ...updates,
      };

      const response = await updateAssessmentApiV1AssessmentAssessmentIdPatch({
        path: { assessment_id: assessmentId },
        body: updateData,
        client: apiClient,
      });

      if ("error" in response && response.error) {
        throw new Error(
          buildApiErrorMessage("Failed to bulk update assessment", response.error)
        );
      }

      return { ...response.data, caseNumber: data.caseNumber };
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: [CLIENT_KEYS.ASSESSMENTS],
      });

      if (variables.caseNumber) {
        queryClient.invalidateQueries({
          queryKey: [CLIENT_KEYS.ASSESSMENTS, "case", variables.caseNumber],
        });
      }

      queryClient.invalidateQueries({
        queryKey: [CLIENT_KEYS.CASES],
      });
    },
    onError: (error) => {
      console.error("Error bulk updating assessment:", error);
    },
  });
};

/**
 * Hook for exporting an assessment by updating both case and assessment status to EXPORTED
 * This makes the case appear in the Edge Compose
 */
/**
 * Hook for updating field-specific statuses
 * This allows marking individual fields as EDITED, COMPLETED, EXPORTED, etc.
 */
export const useUpdateFieldStatuses = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: {
      assessmentId: string;
      caseNumber?: string;
      fieldStatuses: {
        column_name: AssessmentColumnNameEnum;
        status: AssessmentResultStatusEnum;
      }[];
    }) => {
      const { assessmentId, fieldStatuses } = data;

      const response =
        await updateFieldStatusesApiV1AssessmentAssessmentIdFieldStatusPatch({
          path: { assessment_id: assessmentId },
          body: { field_statuses: fieldStatuses },
          client: apiClient,
        });

      if ("error" in response && response.error) {
        throw new Error(
          buildApiErrorMessage("Failed to update field statuses", response.error)
        );
      }

      return { ...response.data, caseNumber: data.caseNumber };
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: [CLIENT_KEYS.ASSESSMENTS],
      });

      // Invalidate field status queries
      queryClient.invalidateQueries({
        queryKey: [
          CLIENT_KEYS.ASSESSMENTS,
          "field-statuses",
          variables.assessmentId,
        ],
      });

      if (variables.caseNumber) {
        queryClient.invalidateQueries({
          queryKey: [CLIENT_KEYS.ASSESSMENTS, "case", variables.caseNumber],
        });
      }
    },
    onError: (error) => {
      console.error("Error updating field statuses:", error);
    },
  });
};

export const useExportAssessment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: { caseId: string; caseNumber: string }) => {
      const { caseId, caseNumber } = data;

      // First, get the assessment for this case
      const assessmentResponse =
        await getAssessmentByCaseNumberApiV1AssessmentCaseCaseNumberGet({
          path: { case_number: caseNumber },
          client: apiClient,
        });

      if ("error" in assessmentResponse && assessmentResponse.error) {
        throw new Error(
          `Failed to get assessment: ${JSON.stringify(assessmentResponse.error)}`
        );
      }

      const assessment = assessmentResponse.data;
      if (!assessment) {
        throw new Error(`Assessment not found for case: ${caseNumber}`);
      }

      // Update the case status to EXPORTED
      const caseResponse = await updateCaseEndpointApiV1CasesCaseIdPatch({
        path: { case_id: caseId },
        query: {
          case_number: caseNumber,
          status: "EXPORTED" as CasesStatusEnum,
        },
        client: apiClient,
      });

      if ("error" in caseResponse && caseResponse.error) {
        throw new Error(
          `Failed to export case: ${JSON.stringify(caseResponse.error)}`
        );
      }

      // Update the assessment status to EXPORTED
      const assessmentUpdateResponse =
        await updateAssessmentApiV1AssessmentAssessmentIdPatch({
          path: { assessment_id: assessment.id },
          body: {
            case_id: caseId,
            status: "EXPORTED" as AssessmentResultStatusEnum,
          },
          client: apiClient,
        });

      if ("error" in assessmentUpdateResponse && assessmentUpdateResponse.error) {
        throw new Error(
          `Failed to export assessment: ${JSON.stringify(
            assessmentUpdateResponse.error
          )}`
        );
      }

      return {
        case: caseResponse.data,
        assessment: assessmentUpdateResponse.data,
      };
    },
    onSuccess: () => {
      // Invalidate cases query to refresh status
      queryClient.invalidateQueries({
        queryKey: [CLIENT_KEYS.CASES],
      });

      // Invalidate assessments query to refresh data
      queryClient.invalidateQueries({
        queryKey: [CLIENT_KEYS.ASSESSMENTS],
      });
    },
    onError: (error) => {
      console.error("Error exporting assessment:", error);
    },
  });
};
